import { useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FilterBar, FilterSearch } from "@/components/ui/filter-bar";
import { UserTable } from "@/components/users/UserTable";
import { useUsers } from "@/hooks/useUsers";

export default function UsersPage() {
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(25);
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const handleSearchChange = useCallback((v: string) => {
    setSearch(v);
    setCursorStack([]);
  }, []);

  const handleLimitChange = (v: string) => {
    setLimit(Number(v));
    setCursorStack([]);
  };

  const cursor = cursorStack.at(-1);
  const { data: result, isLoading, error } = useUsers({ search, cursor, limit });
  const users = result?.data ?? [];
  const hasMore = result?.hasMore ?? false;
  const nextCursor = result?.nextCursor ?? null;

  const goNext = () => {
    if (nextCursor) setCursorStack((prev) => [...prev, nextCursor]);
  };
  const goPrev = () => setCursorStack((prev) => prev.slice(0, -1));
  const page = cursorStack.length + 1;

  return (
    <div>
      <PageHeader title="Użytkownicy" />

      <FilterBar>
        <FilterSearch
          value={search}
          onChange={handleSearchChange}
          placeholder="Szukaj po emailu lub nazwie..."
        />
      </FilterBar>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-12 rounded bg-slate-100 animate-pulse" />
          ))}
        </div>
      )}
      {error && <p className="text-destructive">Błąd ładowania: {error.message}</p>}
      {!isLoading && users.length === 0 && (
        <p className="text-center py-12 text-muted-foreground">Brak użytkowników.</p>
      )}
      {!isLoading && users.length > 0 && <UserTable users={users} />}

      {!isLoading && (users.length > 0 || cursorStack.length > 0) && (
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Strona {page}</span>
            <Select value={String(limit)} onValueChange={handleLimitChange}>
              <SelectTrigger className="w-24 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span>na stronę</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goPrev}
              disabled={cursorStack.length === 0}
            >
              <ChevronLeft className="h-4 w-4" />
              Poprzednia
            </Button>
            <Button variant="outline" size="sm" onClick={goNext} disabled={!hasMore}>
              Następna
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
